import type {
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes'
import { codeFromCodeableConcept } from '#src/codeableConcept.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedQuestionnaire } from '#src/models/questionnaire.ts'
import {
  VALIDATED_TOOLS_ARRAY,
  validatedToolScoreCategory,
} from '#src/models/questionnaires/index.ts'
import { extractQuestionnaireItemsRecursive } from '#src/questionnaire.ts'
import {
  QUESTIONNAIRE_ITEM_CONTROL_EXTENSION_URL,
  QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
} from '#src/structureDefinitions.ts'

export class ValidatedTool extends ExtendedQuestionnaire {
  static buildAll(): ValidatedTool[] {
    return VALIDATED_TOOLS_ARRAY.map((tool) => new ValidatedTool(tool))
  }

  get name(): string {
    invariant(this.resource.name, 'Validated tool must have a name')
    return this.resource.name
  }

  get pdfUrl(): string | undefined {
    return this.findItem(QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL)?.extension?.[0]
      ?.valueString
  }

  // undefined when Orbit computes no category for this tool or this score.
  scoreCategory(score: number): string | undefined {
    return validatedToolScoreCategory(this.resource.url, score)
  }

  get formItems(): QuestionnaireItem[] {
    return extractQuestionnaireItemsRecursive(this.resource.item ?? [])
  }

  // A recorded answer may carry only a code: FHIR does not require the
  // response to copy the option's display back. Match it to the option the
  // instrument defines so a reader that was not handed the option catalogue
  // can still name the selected value.
  findCodingAnswerOption(
    linkId: string,
    answer: QuestionnaireResponseItemAnswer
  ): QuestionnaireItemAnswerOption | undefined {
    const answerCoding = answer.valueCoding
    if (!answerCoding?.code) {
      return undefined
    }
    return this.findItem(linkId)?.answerOption?.find((option) => {
      const optionCoding = option.valueCoding
      if (!optionCoding || optionCoding.code !== answerCoding.code) {
        return false
      }
      // An absent system on either side is not a mismatch.
      return (
        !optionCoding.system ||
        !answerCoding.system ||
        optionCoding.system === answerCoding.system
      )
    })
  }

  getItemControl(linkId: string): 'radio' | 'dropdown' {
    const item = this.getItem(linkId)
    const itemControlExtension = item?.extension?.find(
      (ext) => ext.url === QUESTIONNAIRE_ITEM_CONTROL_EXTENSION_URL
    )
    const controlCode = codeFromCodeableConcept(
      itemControlExtension?.valueCodeableConcept
    )

    return controlCode === 'drop-down' ? 'dropdown' : 'radio'
  }
}
